<template>
  <div class="ad-insights-panel">
    <div v-if="loading" class="ad-insights__state">
      <v-progress-circular indeterminate color="primary" />
      <div class="mt-3">Lade Anzeigen-Daten…</div>
    </div>

    <div v-else-if="error" class="ad-insights__state">
      <v-icon color="error" large>mdi-alert-circle-outline</v-icon>
      <div class="mt-3">Anzeigen-Daten konnten nicht geladen werden.</div>
      <v-btn class="mt-3" small outlined color="primary" @click="load">Erneut versuchen</v-btn>
    </div>

    <template v-else-if="activeChannels.length">
      <!-- Call-to-Action Board -->
      <div class="ad-insights__section-title">Was ist zu tun?</div>

      <div v-if="actionCards.length" class="ad-insights__actions">
        <div v-for="(card, index) in actionCards" :key="index" class="ad-insights__action" :class="`ad-insights__action--${card.type}`">
          <v-icon small :color="card.iconColor">{{ card.icon }}</v-icon>
          <div class="ad-insights__action-info">
            <div class="ad-insights__action-title">{{ card.title }}</div>
            <div class="ad-insights__action-detail">{{ card.detail }}</div>
          </div>
          <div class="ad-insights__action-buttons">
            <v-btn v-if="card.edit" x-small outlined color="primary" @click="openEditor(card.edit)">Umsetzen</v-btn>
            <v-btn v-if="card.link" icon x-small :href="card.link" target="_blank" title="In der Plattform öffnen">
              <v-icon small>mdi-open-in-new</v-icon>
            </v-btn>
          </div>
        </div>
      </div>
      <div v-else class="ad-insights__hint mt-2">Keine dringenden Auffälligkeiten im gewählten Zeitraum. 👍</div>

      <!-- Änderungen & Auswirkungen -->
      <div class="ad-insights__section-title mt-4">
        <v-icon x-small color="primary" class="mr-1">mdi-history</v-icon>
        Änderungen &amp; Auswirkungen <span class="ad-insights__hint">(letzte 14 Tage)</span>
      </div>
      <div v-if="changesLoading" class="ad-insights__hint">
        <v-progress-circular indeterminate size="12" width="2" class="mr-1" /> Änderungs-Historie wird geladen…
      </div>
      <div v-else-if="changesError" class="ad-insights__hint error--text">
        Änderungs-Historie konnte nicht geladen werden.
        <v-btn x-small text color="primary" @click="loadChanges">Erneut versuchen</v-btn>
      </div>
      <template v-else-if="changes.length">
        <div class="ad-insights__grid">
        <div v-for="(change, changeIndex) in visibleChanges" :key="changeIndex" class="ad-insights__change">
          <v-icon small :color="changeIcon(change).color">{{ changeIcon(change).icon }}</v-icon>
          <div class="ad-insights__change-info">
            <div class="ad-insights__change-head">
              <span class="ad-insights__channel-badge" :class="`ad-insights__channel-badge--${change.channel}`">{{ change.channel === "facebook" ? "FB" : change.channel === "google" ? "G" : "TT" }}</span>
              <span class="ad-insights__ellipsis font-weight-bold">{{ change.entityName }}</span>
              <span class="ad-insights__hint">{{ formatChangeDate(change.date) }}</span>
            </div>
            <div class="ad-insights__change-what">{{ changeLabel(change) }}</div>
            <div v-if="change.impact && change.impact.tooEarly" class="ad-insights__hint">Auswirkung: noch zu früh für eine Bewertung (min. 2 Tage).</div>
            <template v-else-if="change.impact">
              <div class="ad-insights__change-impact">{{ impactSummary(change).detail }}</div>
              <div v-if="impactSummary(change).effectText" class="ad-insights__change-effect" :class="impactSummary(change).effectClass">
                {{ impactSummary(change).effectText }}
              </div>
            </template>
          </div>
        </div>
        </div>
        <v-btn v-if="changes.length > 6" x-small text color="primary" @click="showAllChanges = !showAllChanges">
          {{ showAllChanges ? "Weniger anzeigen" : `Alle ${changes.length} Änderungen anzeigen` }}
        </v-btn>
      </template>
      <div v-else class="ad-insights__hint">Keine Änderungen in den letzten 14 Tagen erkannt.</div>

      <v-divider class="my-4" />

      <v-tabs v-model="activeTab" grow>
        <v-tab v-for="ch in channelViews" :key="ch.key">
          {{ ch.label }}
          <v-chip v-if="ch.burners.length" class="ml-2" x-small color="error" dark>{{ ch.burners.length }}</v-chip>
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab">
        <v-tab-item v-for="ch in channelViews" :key="ch.key">
          <!-- Top / Flop -->
          <v-row class="mt-1" dense>
            <v-col v-for="section in ['top', 'flop']" :key="section" cols="12" md="6">
              <div class="ad-insights__section-title">
                <v-icon x-small :color="section === 'top' ? 'green darken-1' : 'orange darken-2'" class="mr-1">
                  {{ section === "top" ? "mdi-trophy-outline" : "mdi-trending-down" }}
                </v-icon>
                {{ section === "top" ? "Top Anzeigen" : "Flop Anzeigen" }}
                <span class="ad-insights__hint">({{ section === "top" ? "nach ROAS" : "nach größtem Verlust" }}, min. {{ euro(ch.minRankSpend) }} Spend)</span>
              </div>
              <div v-for="ad in ch[section]" :key="ad.id" class="ad-insights__rank-row">
                <div class="ad-insights__rank-info">
                  <div class="ad-insights__rank-name">
                    <span v-if="ch.showBadges" class="ad-insights__channel-badge" :class="`ad-insights__channel-badge--${ad.channelKey}`">{{ ad.channelBadge }}</span>
                    <span class="ad-insights__ellipsis">{{ ad.name }}</span>
                    <span v-if="statusLabel(ad.status)" class="ad-insights__status-chip">{{ statusLabel(ad.status) }}</span>
                  </div>
                  <div class="ad-insights__campaign ad-insights__ellipsis">{{ ad.campaign }}</div>
                  <div class="ad-insights__metrics">
                    {{ euro(ad.spend) }} · {{ number(ad.purchases, 0) }} Käufe · CPA {{ ad.cpa === null ? "–" : euro(ad.cpa) }} · CTR {{ number(ad.ctr) }} %<template v-if="section === 'flop' && ad.loss > 0"> · <span class="error--text font-weight-bold">Verlust ~{{ euro(ad.loss) }}</span></template>
                  </div>
                </div>
                <div class="ad-insights__rank-numbers">
                  <span class="ad-insights__roas-chip ad-insights__roas-chip--big" :style="{ backgroundColor: roasColor(ad.roas) }">{{ number(ad.roas) }}</span>
                  <v-btn icon x-small title="Bearbeiten" @click="openEditor(adEdit(ad))"><v-icon x-small>mdi-pencil</v-icon></v-btn>
                  <v-btn v-if="ad.link" icon x-small :href="ad.link" target="_blank"><v-icon x-small>mdi-open-in-new</v-icon></v-btn>
                </div>
              </div>
              <div v-if="!ch[section].length" class="ad-insights__hint">Keine Anzeigen über der Spend-Schwelle.</div>
            </v-col>
          </v-row>

          <!-- Kampagnen: Budget vs. Ergebnis -->
          <div class="ad-insights__section-title mt-4">
            <v-icon x-small color="primary" class="mr-1">mdi-bullhorn-outline</v-icon>
            Kampagnen – Budgetanteil vs. Umsatzanteil
          </div>
          <div class="ad-insights__grid">
          <div v-for="camp in ch.campaigns" :key="`${camp.channelKey}-${camp.name}`" class="ad-insights__campaign-row">
            <div class="ad-insights__campaign-head">
              <div class="ad-insights__rank-name">
                <span v-if="ch.showBadges" class="ad-insights__channel-badge" :class="`ad-insights__channel-badge--${camp.channelKey}`">{{ camp.channelBadge }}</span>
                <span class="ad-insights__ellipsis">{{ camp.name }}</span>
                <span v-if="statusLabel(camp.status)" class="ad-insights__status-chip">{{ statusLabel(camp.status) }}</span>
              </div>
              <div class="ad-insights__campaign-chips">
                <span class="ad-insights__roas-chip" :style="{ backgroundColor: roasColor(camp.roas) }">ROAS {{ number(camp.roas) }}</span>
                <v-btn v-if="camp.id" icon x-small title="Bearbeiten" @click="openEditor(campaignEdit(camp))"><v-icon x-small>mdi-pencil</v-icon></v-btn>
                <v-btn v-if="camp.link" icon x-small :href="camp.link" target="_blank"><v-icon x-small>mdi-open-in-new</v-icon></v-btn>
              </div>
            </div>
            <div class="ad-insights__campaign-meta">
              <span v-if="camp.dailyBudget !== null">Budget {{ euro(camp.dailyBudget) }}/Tag</span>
              <span v-if="camp.targetRoas">· Ziel-ROAS {{ number(camp.targetRoas) }}</span>
            </div>
            <div class="ad-insights__metrics">
              {{ euro(camp.spend) }} Spend · {{ euro(camp.purchaseValue) }} Umsatz · {{ number(camp.purchases, 0) }} Käufe
              · CPA {{ camp.purchases ? euro(camp.spend / camp.purchases) : "–" }}
            </div>
            <div class="ad-insights__bar-row">
              <span class="ad-insights__bar-label">Spend {{ percent(camp.spendShare) }}</span>
              <div class="ad-insights__bar"><div class="ad-insights__bar-fill ad-insights__bar-fill--spend" :style="{ width: (camp.spendShare * 100) + '%' }"/></div>
            </div>
            <div class="ad-insights__bar-row">
              <span class="ad-insights__bar-label">Umsatz {{ percent(camp.valueShare) }}</span>
              <div class="ad-insights__bar"><div class="ad-insights__bar-fill ad-insights__bar-fill--value" :style="{ width: (camp.valueShare * 100) + '%' }"/></div>
            </div>
          </div>
          </div>

          <!-- Alle Anzeigen -->
          <v-expansion-panels flat class="mt-4">
            <v-expansion-panel>
              <v-expansion-panel-header class="px-0">Alle Anzeigen ({{ ch.ads.length }})</v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-data-table
                  dense
                  mobile-breakpoint="0"
                  :headers="tableHeaders"
                  :items="ch.ads"
                  :items-per-page="15"
                  sort-by="spend"
                  sort-desc
                >
                  <template v-slot:item.name="{ item }">
                    <div class="ad-insights__rank-name">
                      <span v-if="ch.showBadges" class="ad-insights__channel-badge" :class="`ad-insights__channel-badge--${item.channelKey}`">{{ item.channelBadge }}</span>
                      <a v-if="item.link" :href="item.link" target="_blank" class="ad-insights__table-link">
                        <div class="ad-insights__ellipsis" style="max-width: 200px">{{ item.name }}</div>
                      </a>
                      <div v-else class="ad-insights__ellipsis" style="max-width: 200px">{{ item.name }}</div>
                      <span v-if="statusLabel(item.status)" class="ad-insights__status-chip">{{ statusLabel(item.status) }}</span>
                    </div>
                    <div class="ad-insights__campaign ad-insights__ellipsis" style="max-width: 220px">{{ item.campaign }}</div>
                  </template>
                  <template v-slot:item.spend="{ item }">{{ euro(item.spend) }}</template>
                  <template v-slot:item.roas="{ item }">
                    <span class="ad-insights__roas-chip" :style="{ backgroundColor: roasColor(item.roas) }">{{ number(item.roas) }}</span>
                  </template>
                  <template v-slot:item.purchases="{ item }">{{ number(item.purchases, 0) }}</template>
                  <template v-slot:item.cpa="{ item }">{{ item.cpa === null ? '–' : euro(item.cpa) }}</template>
                  <template v-slot:item.ctr="{ item }">{{ number(item.ctr) }} %</template>
                  <template v-slot:item.edit="{ item }">
                    <v-btn icon x-small title="Bearbeiten" @click="openEditor(adEdit(item))"><v-icon x-small>mdi-pencil</v-icon></v-btn>
                  </template>
                </v-data-table>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-tab-item>
      </v-tabs-items>
    </template>

    <div v-else class="ad-insights__state">
      <v-icon large>mdi-tray-remove</v-icon>
      <div class="mt-3">Keine Anzeigen mit Spend im gewählten Zeitraum.</div>
    </div>

    <AdEditDialog
      v-model="editOpen"
      :shop-code="shopCode"
      :target="editTarget"
      :recommendation="editRecommendation"
      :break-even-roas="breakEvenRoas"
      @saved="load"
    />
  </div>
</template>

<script>
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../../fb";
import { mapGetters } from "vuex";
import { getDecimalNumberString, getRateColor } from "@/utils";
import AdEditDialog from "./AdEditDialog";

const BURNER_MIN_SPEND = 15;
const RANK_MIN_SPEND = 10;
const ACTION_MIN_SPEND = 25;

export default {
  name: "AdInsightsPanel",
  components: { AdEditDialog },
  props: {
    shopCode: String,
    active: {
      type: Boolean,
      default: false,
    },
    breakEvenRoas: {
      type: Number,
      default: null,
    },
    shopRevenue: {
      type: Number,
      default: null,
    },
    shopProfit: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      error: false,
      insights: null,
      changes: [],
      changesLoading: false,
      changesError: false,
      showAllChanges: false,
      activeTab: 0,
      editOpen: false,
      editTarget: null,
      editRecommendation: null,
      tableHeaders: [
        { text: "Anzeige", value: "name" },
        { text: "Spend", value: "spend" },
        { text: "ROAS", value: "roas" },
        { text: "Käufe", value: "purchases" },
        { text: "CPA", value: "cpa" },
        { text: "CTR", value: "ctr" },
        { text: "", value: "edit", sortable: false, align: "end" },
      ],
    };
  },
  computed: {
    ...mapGetters(["statisticRange"]),
    activeChannels() {
      if (!this.insights) return [];
      const labels = { facebook: "Facebook", google: "Google", tiktok: "TikTok" };
      const badges = { facebook: "FB", google: "G", tiktok: "TT" };

      return Object.entries(this.insights.byChannel)
        .filter(([, data]) => (data.ads || []).length > 0)
        .map(([key, data]) => {
          const ads = data.ads.map((ad) => ({
            ...ad,
            channelKey: key,
            channelBadge: badges[key] || key,
            // Estimated euro loss after all costs: spend minus the revenue share
            // that is available to cover ads (via break-even ROAS).
            loss: +(ad.spend - ad.purchaseValue / (this.breakEvenRoas || 1)).toFixed(2),
          }));

          const totalSpend = ads.reduce((sum, ad) => sum + ad.spend, 0);
          const totalValue = ads.reduce((sum, ad) => sum + ad.purchaseValue, 0);
          // Threshold scales with account size: 1% of channel spend, 10-250 €.
          const minRankSpend = Math.min(250, Math.max(RANK_MIN_SPEND, totalSpend * 0.01));
          const ranked = ads.filter((ad) => ad.spend >= minRankSpend);
          const byRoasDesc = [...ranked].sort((a, b) => b.roas - a.roas);

          return {
            key,
            label: labels[key] || key,
            minRankSpend,
            ads,
            // Active burners are open to-dos; paused ones show up as "erledigt".
            burners: ads.filter((ad) => ad.spend >= BURNER_MIN_SPEND && ad.purchases === 0 && this.isAdActive(ad)),
            doneBurners: ads.filter((ad) => ad.spend >= BURNER_MIN_SPEND && ad.purchases === 0 && !this.isAdActive(ad)),
            top: byRoasDesc.slice(0, 5),
            // Flops ranked by biggest estimated euro loss, not lowest ROAS.
            flop: [...ranked].sort((a, b) => b.loss - a.loss).slice(0, 5),
            campaigns: (data.campaigns || []).map((camp) => ({
              ...camp,
              channelKey: key,
              channelBadge: badges[key] || key,
              spendShare: totalSpend ? camp.spend / totalSpend : 0,
              valueShare: totalValue ? camp.purchaseValue / totalValue : 0,
            })),
          };
        });
    },
    // Default tab: all channels merged — nothing gets lost between tabs.
    channelViews() {
      const channels = this.activeChannels;
      if (channels.length <= 1) return channels;

      const allAds = channels.flatMap((ch) => ch.ads);
      const totalSpend = allAds.reduce((sum, ad) => sum + ad.spend, 0);
      const totalValue = allAds.reduce((sum, ad) => sum + ad.purchaseValue, 0);
      const minRankSpend = Math.min(250, Math.max(RANK_MIN_SPEND, totalSpend * 0.01));
      const ranked = allAds.filter((ad) => ad.spend >= minRankSpend);
      const byRoasDesc = [...ranked].sort((a, b) => b.roas - a.roas);

      const combined = {
        key: "all",
        label: "Alle",
        showBadges: true,
        minRankSpend,
        ads: [...allAds].sort((a, b) => b.spend - a.spend),
        burners: channels.flatMap((ch) => ch.burners),
        doneBurners: channels.flatMap((ch) => ch.doneBurners),
        top: byRoasDesc.slice(0, 5),
        flop: [...ranked].sort((a, b) => b.loss - a.loss).slice(0, 5),
        campaigns: channels
          .flatMap((ch) => ch.campaigns)
          .map((camp) => ({
            ...camp,
            spendShare: totalSpend ? camp.spend / totalSpend : 0,
            valueShare: totalValue ? camp.purchaseValue / totalValue : 0,
          }))
          .sort((a, b) => b.spend - a.spend),
      };
      return [combined, ...channels];
    },
    visibleChanges() {
      return this.showAllChanges ? this.changes : this.changes.slice(0, 6);
    },
    actionCards() {
      const cards = [];

      this.activeChannels.forEach((ch) => {
        ch.burners.forEach((ad) => {
          cards.push({
            severity: 1,
            type: "error",
            icon: "mdi-pause-octagon",
            iconColor: "error",
            title: `Pausieren prüfen: „${ad.name}"`,
            detail: `${ch.label}: ${this.euro(ad.spend)} Spend ohne einen einzigen Kauf.`,
            link: ad.link,
            edit: this.adEdit(ad, {
              text: `Empfehlung: Anzeige pausieren — ${this.euro(ad.spend)} Spend ohne Kauf.`,
              status: "PAUSED",
            }),
          });
        });

        // Already paused burners: shown as completed to-dos instead of vanishing.
        ch.doneBurners.forEach((ad) => {
          cards.push({
            severity: 9,
            type: "done",
            icon: "mdi-check-circle",
            iconColor: "success",
            title: `Erledigt – pausiert: „${ad.name}"`,
            detail: `${ch.label}: ${this.euro(ad.spend)} Spend ohne Kauf im Zeitraum — Anzeige ist inzwischen pausiert.`,
            link: ad.link,
          });
        });

        if (this.breakEvenRoas) {
          ch.ads
            .filter((ad) => ad.spend >= ACTION_MIN_SPEND && ad.purchases > 0 && ad.roas < this.breakEvenRoas)
            .forEach((ad) => {
              if (this.isAdActive(ad)) {
                cards.push({
                  severity: 2,
                  type: "warning",
                  icon: "mdi-trending-down",
                  iconColor: "orange darken-2",
                  title: `Unter Break-even: „${ad.name}"`,
                  detail: `${ch.label}: ROAS ${this.number(ad.roas)} liegt unter eurem Break-even von ${this.number(this.breakEvenRoas)} — ${this.euro(ad.spend)} Spend, CPA ${ad.cpa === null ? "–" : this.euro(ad.cpa)}. Verliert nach allen Kosten Geld.`,
                  link: ad.link,
                  edit: this.adEdit(ad, {
                    text: `Empfehlung: pausieren oder Creative tauschen — ROAS ${this.number(ad.roas)} liegt unter Break-even (${this.number(this.breakEvenRoas)}).`,
                    status: "PAUSED",
                  }),
                });
              } else {
                cards.push({
                  severity: 9,
                  type: "done",
                  icon: "mdi-check-circle",
                  iconColor: "success",
                  title: `Erledigt – pausiert: „${ad.name}"`,
                  detail: `${ch.label}: lief mit ROAS ${this.number(ad.roas)} unter Break-even (${this.number(this.breakEvenRoas)}) — Anzeige ist inzwischen pausiert.`,
                  link: ad.link,
                });
              }
            });
        }

        ch.campaigns.forEach((camp) => {
          if (camp.spend < ACTION_MIN_SPEND) return;
          const campCpa = camp.purchases ? ` · CPA ${this.euro(camp.spend / camp.purchases)}` : "";
          const campaignPaused = camp.status && camp.status !== "ACTIVE";

          // Paused campaigns: show as done if they matched a warning rule; the
          // scale suggestion is simply irrelevant for paused campaigns.
          if (campaignPaused) {
            const matchedWarning =
              (camp.targetRoas && camp.roas < camp.targetRoas * 0.8) ||
              (camp.spend >= 50 && camp.spendShare - camp.valueShare >= 0.15) ||
              (this.breakEvenRoas && camp.roas < this.breakEvenRoas);
            if (matchedWarning) {
              cards.push({
                severity: 9,
                type: "done",
                icon: "mdi-check-circle",
                iconColor: "success",
                title: `Erledigt – Kampagne pausiert: „${camp.name}"`,
                detail: `ROAS ${this.number(camp.roas)} bei ${this.euro(camp.spend)} Spend${campCpa} — Kampagne ist inzwischen pausiert.`,
                link: camp.link,
              });
            }
            return;
          }

          if (camp.targetRoas && camp.roas < camp.targetRoas * 0.8) {
            cards.push({
              severity: 2,
              type: "warning",
              icon: "mdi-target",
              iconColor: "orange darken-2",
              title: `Ziel-ROAS verfehlt: „${camp.name}"`,
              detail: `Ist ${this.number(camp.roas)} vs. Ziel ${this.number(camp.targetRoas)}${camp.dailyBudget !== null ? ` bei ${this.euro(camp.dailyBudget)}/Tag Budget` : ""}${campCpa} — Budget senken oder Gebotsstrategie prüfen.`,
              link: camp.link,
              edit: camp.id ? this.campaignEdit(camp, camp.dailyBudget ? {
                text: `Empfehlung: Budget um ~30 % senken auf ${Math.round(camp.dailyBudget * 0.7)} €/Tag, bis der ROAS sich erholt.`,
                dailyBudget: Math.round(camp.dailyBudget * 0.7),
              } : { text: "Empfehlung: Gebotsstrategie bzw. Ziel-ROAS prüfen." }) : null,
            });
          } else if (camp.roas >= Math.max(camp.targetRoas || 0, this.breakEvenRoas || 0, 2) * 1.3) {
            cards.push({
              severity: 3,
              type: "success",
              icon: "mdi-rocket-launch",
              iconColor: "green darken-1",
              title: `Skalieren prüfen: „${camp.name}"`,
              detail: `ROAS ${this.number(camp.roas)} liegt deutlich über ${camp.targetRoas ? "dem Ziel (" + this.number(camp.targetRoas) + ")" : "Break-even"}${camp.dailyBudget !== null ? ` — Budget aktuell ${this.euro(camp.dailyBudget)}/Tag` : ""}${campCpa}. Mehr Budget könnte profitablen Umsatz freisetzen.`,
              link: camp.link,
              edit: camp.id ? this.campaignEdit(camp, camp.dailyBudget ? {
                text: `Empfehlung: Budget um ~20 % erhöhen auf ${Math.round(camp.dailyBudget * 1.2)} €/Tag.`,
                dailyBudget: Math.round(camp.dailyBudget * 1.2),
              } : null) : null,
            });
          }

          if (camp.spend >= 50 && camp.spendShare - camp.valueShare >= 0.15) {
            cards.push({
              severity: 2,
              type: "warning",
              icon: "mdi-swap-horizontal",
              iconColor: "orange darken-2",
              title: `Umschichten: „${camp.name}"`,
              detail: `${ch.label}: nimmt ${this.percent(camp.spendShare)} des Budgets, liefert aber nur ${this.percent(camp.valueShare)} des Umsatzes.`,
              link: camp.link,
              edit: camp.id ? this.campaignEdit(camp, camp.dailyBudget ? {
                text: `Empfehlung: Budget um ~30 % senken auf ${Math.round(camp.dailyBudget * 0.7)} €/Tag und in stärkere Kampagnen umschichten.`,
                dailyBudget: Math.round(camp.dailyBudget * 0.7),
              } : null) : null,
            });
          }
        });
      });

      // Open to-dos first, completed ones at the bottom.
      return cards.sort((a, b) => a.severity - b.severity).slice(0, 10);
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(active) {
        if (active) this.load();
      },
    },
    shopCode() {
      if (this.active) this.load();
    },
  },
  methods: {
    // Build the edit-dialog payload for an ad row (status only — budgets live
    // on adset/campaign level).
    adEdit(ad, recommendation = null) {
      return {
        target: {
          channel: ad.channelKey,
          entityType: "ad",
          entityId: ad.id,
          adGroupId: ad.adGroupId || null,
          name: ad.name,
          campaign: ad.campaign,
          status: ad.status,
          dailyBudget: null,
          targetRoas: null,
          link: ad.link,
        },
        recommendation,
      };
    },
    campaignEdit(camp, recommendation = null) {
      return {
        target: {
          channel: camp.channelKey,
          entityType: "campaign",
          entityId: camp.id,
          adGroupId: null,
          name: camp.name,
          campaign: null,
          status: camp.status,
          dailyBudget: camp.dailyBudget,
          targetRoas: camp.targetRoas,
          link: camp.link,
        },
        recommendation,
      };
    },
    openEditor(edit) {
      this.editTarget = edit.target;
      this.editRecommendation = edit.recommendation || null;
      this.editOpen = true;
    },
    async load() {
      this.loading = true;
      this.error = false;
      this.activeTab = 0;
      this.insights = null;
      this.$emit("loaded", null);
      this.loadChanges();
      try {
        const getAdInsights = httpsCallable(functions, "getAdInsights", { timeout: 180000 });
        const res = await getAdInsights({
          shopCode: this.shopCode,
          created_at_min: this.statisticRange.min,
          created_at_max: this.statisticRange.max,
        });
        this.insights = res.data;
        this.$emit("loaded", this.insights);
      } catch (e) {
        this.error = true;
      }
      this.loading = false;
    },
    // Non-blocking: the change feed loads in parallel with the insights.
    async loadChanges() {
      this.changesLoading = true;
      this.changesError = false;
      this.changes = [];
      this.showAllChanges = false;
      try {
        const getChanges = httpsCallable(functions, "getAdChanges", { timeout: 180000 });
        const res = await getChanges({ shopCode: this.shopCode });
        this.changes = res.data.changes || [];
      } catch (e) {
        this.changesError = true;
      }
      this.changesLoading = false;
    },
    changeLabel(change) {
      if (change.changeType === "budget") {
        return `Budget geändert: ${change.oldValue !== null ? this.euro(change.oldValue) : "?"} → ${this.euro(change.newValue)}/Tag`;
      }
      if (change.changeType === "target_roas") {
        return `Ziel-ROAS geändert: ${change.oldValue !== null ? this.number(change.oldValue) : "?"} → ${this.number(change.newValue)}`;
      }
      if (change.changeType === "bid") return "Gebotsstrategie geändert";
      return `Status: ${change.oldValue || "?"} → ${change.newValue || "?"}`;
    },
    changeIcon(change) {
      if (change.changeType === "budget") return { icon: "mdi-cash-multiple", color: "blue darken-1" };
      if (change.changeType === "target_roas") return { icon: "mdi-target", color: "deep-purple" };
      if (change.changeType === "bid") return { icon: "mdi-gavel", color: "blue-grey" };
      const paused = /inaktiv|paused/i.test(`${change.newValue}`);
      return paused
        ? { icon: "mdi-pause-circle-outline", color: "grey darken-1" }
        : { icon: "mdi-play-circle-outline", color: "green darken-1" };
    },
    formatChangeDate(date) {
      return new Date(date).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
    },
    impactClass(after, before) {
      if (after > before) return "success--text";
      if (after < before) return "error--text";
      return "";
    },
    // Estimated profit contribution per day: revenue share available for ads
    // (via break-even ROAS) minus ad spend.
    profitPerDay(window) {
      if (!this.breakEvenRoas || !window) return null;
      return +((window.spendPerDay * window.roas) / this.breakEvenRoas - window.spendPerDay).toFixed(2);
    },
    isPauseChange(change) {
      return change.changeType === "status" && /inaktiv|paused/i.test(`${change.newValue}`);
    },
    isActivateChange(change) {
      return change.changeType === "status" && !this.isPauseChange(change) && /aktiv|active|enabled/i.test(`${change.newValue}`);
    },
    // Translates the raw before/after windows into a bottom-line statement.
    impactSummary(change) {
      const impact = change.impact;
      const before = impact.before;
      const after = impact.after;
      const window = (win) => `ROAS ${this.number(win.roas)} · ${this.number(win.spendPerDay)} €/Tag${win.cpa !== null ? ` · CPA ${this.euro(win.cpa)}` : ""}`;

      // Pause: the after-window is only residual delivery — what matters is
      // what the paused entity was doing before.
      if (this.isPauseChange(change)) {
        const profitBefore = this.profitPerDay(before);
        if (profitBefore === null) return { detail: `Davor (${impact.windowDays} Tage): ${window(before)}`, effectText: null };
        if (profitBefore < -0.5) {
          return {
            detail: `Davor (${impact.windowDays} Tage): ${window(before)}`,
            effectText: `✓ Stoppt ~${this.number(Math.abs(profitBefore))} €/Tag Verlust (≈ ${this.number(Math.abs(profitBefore) * 7)} €/Woche)`,
            effectClass: "success--text",
          };
        }
        if (profitBefore > 0.5) {
          return {
            detail: `Davor (${impact.windowDays} Tage): ${window(before)}`,
            effectText: `⚠ Achtung: war profitabel (~+${this.number(profitBefore)} €/Tag Gewinn entfällt)`,
            effectClass: "warning--text text--darken-2",
          };
        }
        return { detail: `Davor (${impact.windowDays} Tage): ${window(before)}`, effectText: "Effekt fürs Gesamtergebnis: neutral (lief ±0)", effectClass: "" };
      }

      // Activation: a before/after comparison against "off" is meaningless —
      // show what it delivers since it runs.
      if (this.isActivateChange(change)) {
        const profitAfter = this.profitPerDay(after);
        return {
          detail: `Seit Aktivierung (${impact.windowDays} Tage): ${window(after)}`,
          effectText: profitAfter === null ? null
            : profitAfter >= 0
              ? `Gewinn-Effekt: +${this.number(profitAfter)} €/Tag (≈ +${this.number(profitAfter * 7)} €/Woche)`
              : `Gewinn-Effekt: −${this.number(Math.abs(profitAfter))} €/Tag — läuft unter Break-even`,
          effectClass: profitAfter !== null && profitAfter >= 0 ? "success--text" : "error--text",
        };
      }

      // Budget / target-ROAS / bid changes: full comparison + profit delta.
      const profitBefore = this.profitPerDay(before);
      const profitAfter = this.profitPerDay(after);
      const detail = `${impact.windowDays}-Tage-Vergleich: ${window(before)} → ${window(after)}`;
      if (profitBefore === null || profitAfter === null) return { detail, effectText: null };
      const delta = +(profitAfter - profitBefore).toFixed(2);
      return {
        detail,
        effectText: `Gewinn-Effekt: ${delta >= 0 ? "+" : "−"}${this.number(Math.abs(delta))} €/Tag (${this.number(profitBefore)} → ${this.number(profitAfter)} €/Tag, ≈ ${delta >= 0 ? "+" : "−"}${this.number(Math.abs(delta) * 7)} €/Woche)`,
        effectClass: Math.abs(delta) < 0.5 ? "" : delta > 0 ? "success--text" : "error--text",
      };
    },
    euro(value) {
      return getDecimalNumberString(value, 2) + " €";
    },
    number(value, fraction = 2) {
      return getDecimalNumberString(value, fraction);
    },
    percent(share) {
      return Math.round(share * 100) + " %";
    },
    roasColor(roas) {
      return getRateColor(3.5, 0, roas);
    },
    isAdActive(ad) {
      return !ad.status || ad.status === "ACTIVE";
    },
    // effective_status → short German label; null while active (no chip needed).
    statusLabel(status) {
      if (!status || status === "ACTIVE") return null;
      if (["PAUSED", "CAMPAIGN_PAUSED", "ADSET_PAUSED"].includes(status)) return "Pausiert";
      if (["REMOVED", "DELETED", "ARCHIVED"].includes(status)) return "Entfernt";
      return status.toLowerCase();
    },
  },
};
</script>

<style lang="scss" scoped>
.ad-insights__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 260px;
  color: #757575;
}

.ad-insights__section-title {
  font-weight: 700;
  font-size: 14px;
  margin: 12px 0 6px;
}

.ad-insights__hint {
  font-weight: lighter;
  font-size: 12px;
  color: #9e9e9e;
}

.ad-insights__ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Responsive: 1 Spalte neben offenem Chat, 2-3 Spalten bei voller Breite. */
.ad-insights__actions,
.ad-insights__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(440px, 100%), 1fr));
  gap: 8px;
  margin-top: 6px;

  > * {
    margin-bottom: 0 !important;
  }
}

.ad-insights__action {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  border-radius: 8px;
  padding: 8px 10px;
  border: 1px solid #ececec;

  &--error {
    background-color: rgba(215, 0, 0, 0.05);
    border-color: rgba(215, 0, 0, 0.25);
  }

  &--warning {
    background-color: rgba(255, 152, 0, 0.06);
    border-color: rgba(255, 152, 0, 0.3);
  }

  &--success {
    background-color: rgba(67, 160, 71, 0.06);
    border-color: rgba(67, 160, 71, 0.3);
  }

  &--done {
    background-color: #fafafa;
    border-color: #e8e8e8;
    opacity: 0.75;

    .ad-insights__action-title {
      color: #616161;
    }
  }
}

.ad-insights__action-info {
  flex: 1;
  min-width: 0;
}

.ad-insights__action-buttons {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.ad-insights__action-title {
  font-size: 13px;
  font-weight: 700;
}

.ad-insights__action-detail {
  font-size: 12px;
  color: #616161;
}

.ad-insights__rank-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 10px;
  border: 1px solid #f0f0f0;
  margin-bottom: 6px;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: #dbe7f5;
    box-shadow: rgba(42, 51, 70, 0.06) 0 2px 8px;
  }
}

.ad-insights__rank-info {
  min-width: 0;
  font-size: 13px;
}

.ad-insights__rank-name {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-weight: 600;
}

.ad-insights__metrics {
  font-size: 11px;
  color: #757575;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ad-insights__change {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  margin-bottom: 6px;
}

.ad-insights__change-info {
  flex: 1;
  min-width: 0;
}

.ad-insights__change-head {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;

  .ad-insights__hint {
    margin-left: auto;
    flex-shrink: 0;
  }
}

.ad-insights__change-what {
  font-size: 12px;
  color: #424242;
  margin-top: 1px;
}

.ad-insights__change-impact {
  font-size: 11px;
  color: #757575;
  margin-top: 2px;
}

.ad-insights__change-effect {
  font-size: 12px;
  font-weight: 700;
  margin-top: 3px;
}

.ad-insights__status-chip {
  flex-shrink: 0;
  font-size: 9px;
  font-weight: 700;
  line-height: 1;
  padding: 3px 6px;
  border-radius: 8px;
  color: #757575;
  background-color: #eeeeee;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.ad-insights__channel-badge {
  flex-shrink: 0;
  font-size: 9px;
  font-weight: 800;
  line-height: 1;
  padding: 3px 5px;
  border-radius: 5px;
  color: white;
  background-color: #9e9e9e;

  &--facebook {
    background-color: #1c6ebb;
  }

  &--google {
    background-color: #b2a92b;
  }

  &--tiktok {
    background-color: #383232;
  }
}

.ad-insights__campaign {
  font-size: 11px;
  color: #9e9e9e;
}

.ad-insights__rank-numbers {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.ad-insights__roas-chip {
  display: inline-block;
  color: #2b2b2b;
  font-weight: 700;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;

  &--big {
    font-size: 13px;
    padding: 4px 10px;
    border-radius: 12px;
  }
}

.ad-insights__spend {
  font-size: 12px;
  font-weight: 600;
  min-width: 64px;
  text-align: right;
}

.ad-insights__campaign-row {
  padding: 8px;
  border: 1px solid #ececec;
  border-radius: 8px;
  margin-bottom: 8px;
}

.ad-insights__campaign-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
}

.ad-insights__campaign-chips {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.ad-insights__campaign-meta {
  font-size: 11px;
  color: #757575;
  margin-bottom: 6px;
}

.ad-insights__table-link {
  text-decoration: none;
  color: #1c6ebb;
}

.ad-insights__bar-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 3px;
}

.ad-insights__bar-label {
  font-size: 11px;
  color: #757575;
  min-width: 92px;
}

.ad-insights__bar {
  flex: 1;
  height: 8px;
  background-color: #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
}

.ad-insights__bar-fill {
  height: 100%;
  border-radius: 4px;

  &--spend {
    background-color: #d70000;
    opacity: 0.65;
  }

  &--value {
    background-color: #43a653;
  }
}
</style>
